import { useEffect } from 'react';
import { apiContracts } from '@responsedock/api-contracts';
import { useAuthStore, type EmbeddedContext } from '../store/auth';
import { useWorkspaceStore } from '../store/workspace';
import { api, apiBaseUrl } from '../lib/api';
import { callApiContract } from '../lib/contractApi';
import type { User, Workspace } from '@responsedock/types';
import { clearStoredEmbedSession, readStoredEmbedSession, storeEmbedSession } from '../lib/embedSession';
import { clearSignupAttribution, readSignupAttribution, type SignupAttribution } from '../lib/attribution';

const LANDING_URL = import.meta.env.VITE_LANDING_URL ?? 'https://responsedock.com';

// Ensure fetchMe only runs once across all hook instances
let authInitialized = false;

interface AuthWorkspaceResult {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
}

function getParentOrigin() {
  const ancestorOrigins = (window.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
  const ancestorOrigin = ancestorOrigins?.[0];
  if (ancestorOrigin) return ancestorOrigin;
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
  return null;
}

function parentOriginIsAllowed(allowedOrigin?: string | null) {
  if (!allowedOrigin || window.parent === window) return false;
  const parentOrigin = getParentOrigin();
  return parentOrigin === allowedOrigin;
}

/** The landing-page attribution keys the API accepts (it strips anything else, e.g. `capturedAt`). */
function attributionPayload(attribution: SignupAttribution) {
  const { capturedAt: _capturedAt, ...params } = attribution;
  return params;
}

/**
 * Google sign-in returns through a redirect that cannot carry the landing-page attribution, so it is
 * sent once the session is known. The API keeps it only for an account created in the last 24 hours
 * that has none yet, and the stored copy is cleared either way so it is sent at most once.
 */
function sendPendingSignupAttribution() {
  const attribution = readSignupAttribution();
  if (!attribution) return;
  clearSignupAttribution();
  void callApiContract(apiContracts.signupAttribution, { attribution: attributionPayload(attribution) }).catch(() => undefined);
}

function routeForEmbeddedContext(embed?: EmbeddedContext | null) {
  if (!embed) return '/dashboard';
  if (embed.role === 'respondent' || embed.mode === 'respondent') return '/embedded/surveys';
  if (embed.mode === 'setup') return '/surveys/new/ai';
  if (embed.mode === 'builder' && embed.surveyId) return `/surveys/${embed.surveyId}/edit`;
  if (embed.mode === 'results' && embed.surveyId) return `/surveys/${embed.surveyId}/analytics`;
  return '/embedded/admin';
}

export function useAuth() {
  const {
    user,
    isLoading,
    isEmbedded,
    embedContext,
    embedError,
    setUser,
    setLoading,
    setIsEmbedded,
    setEmbedBearerToken,
    setEmbedContext,
    setEmbedError,
    useMockData,
    setUseMockData,
  } = useAuthStore();
  const { setWorkspaces, setActiveWorkspace, activeWorkspaceId } = useWorkspaceStore();

  async function loadWorkspaces(): Promise<AuthWorkspaceResult> {
    const wsRes = await api.get<{ workspaces: Workspace[] }>('/workspaces');
    const workspaces = wsRes.data.workspaces;
    setWorkspaces(workspaces);
    const nextActiveWorkspaceId = workspaces.some((workspace) => workspace.id === activeWorkspaceId)
      ? activeWorkspaceId
      : workspaces[0]?.id ?? null;
    if (nextActiveWorkspaceId) {
      setActiveWorkspace(nextActiveWorkspaceId);
    }
    return { workspaces, activeWorkspaceId: nextActiveWorkspaceId };
  }

  useEffect(() => {
    if (authInitialized) return;

    // ── Embed SSO: exchange ?embedToken= for a session bearer token ──────────
    const params = new URLSearchParams(window.location.search);
    const embedToken = params.get('embedToken');
    if (embedToken) {
      authInitialized = true;
      async function exchangeEmbedToken() {
        try {
          const res = await api.post<{ token: string; user: User; workspaces: Workspace[]; embed: EmbeddedContext }>(
            '/auth/embed-session',
            { token: embedToken, parentOrigin: getParentOrigin() },
          );
          if (!parentOriginIsAllowed(res.data.embed.allowedOrigin)) {
            throw new Error('This embedded workspace is not allowed on the current parent domain.');
          }
          setEmbedBearerToken(res.data.token);
          setIsEmbedded(true);
          setEmbedContext(res.data.embed);
          setEmbedError(null);
          setUser(res.data.user);
          setWorkspaces(res.data.workspaces);
          if (res.data.workspaces[0]) {
            setActiveWorkspace(res.data.workspaces[0].id);
          }
          storeEmbedSession({ token: res.data.token, user: res.data.user, workspaces: res.data.workspaces, embed: res.data.embed });
          // Strip the embed token from the URL so it doesn't leak into history
          params.delete('embedToken');
          const newSearch = params.toString();
          const route = routeForEmbeddedContext(res.data.embed);
          const currentPath = window.location.pathname;
          const targetPath = currentPath === '/' || currentPath === '/dashboard' || currentPath === '/login'
            ? route
            : currentPath;
          window.history.replaceState(
            {},
            '',
            targetPath + (newSearch ? `?${newSearch}` : ''),
          );
          window.dispatchEvent(new PopStateEvent('popstate'));
        } catch (error) {
          setIsEmbedded(true);
          setEmbedBearerToken(null);
          setEmbedContext(null);
          setEmbedError(error instanceof Error ? error.message : 'Unable to start the embedded session.');
          setUser(null);
        } finally {
          setLoading(false);
        }
      }
      void exchangeEmbedToken();
      return;
    }

    const storedEmbed = readStoredEmbedSession();
    if (storedEmbed && parentOriginIsAllowed(storedEmbed.embed.allowedOrigin)) {
      authInitialized = true;
      setEmbedBearerToken(storedEmbed.token);
      setIsEmbedded(true);
      setEmbedContext(storedEmbed.embed);
      setEmbedError(null);
      setUser(storedEmbed.user);
      setWorkspaces(storedEmbed.workspaces);
      if (storedEmbed.workspaces[0]) setActiveWorkspace(storedEmbed.workspaces[0].id);
      void api.get('/auth/me').then(() => setLoading(false)).catch(() => {
        clearStoredEmbedSession();
        setEmbedBearerToken(null);
        setEmbedContext(null);
        setEmbedError('The embedded session expired. Reload it from the hosting service.');
        setUser(null);
        setLoading(false);
      });
      return;
    }
    if (storedEmbed) clearStoredEmbedSession();

    // ── Normal cookie-based auth ──────────────────────────────────────────────
    authInitialized = true;

    async function fetchMe() {
      try {
        const meRes = await api.get<{ user: User }>('/auth/me');
        setUser(meRes.data.user);
        if (meRes.data.user && !meRes.data.user.impersonator) sendPendingSignupAttribution();
        await loadWorkspaces();
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    }
    void fetchMe();
  }, []);

  function login() {
    window.location.href = `${apiBaseUrl}/auth/google`;
  }

  async function loginWithEmail(email: string, password: string): Promise<AuthWorkspaceResult> {
    const payload = await callApiContract(apiContracts.login, { email, password });
    authInitialized = true;
    setUser(payload.user as User);
    setLoading(false);
    return loadWorkspaces();
  }

  async function registerWithEmail(name: string, email: string, password: string, options: { useCase?: string | null } = {}): Promise<AuthWorkspaceResult> {
    const attribution = readSignupAttribution();
    const payload = await callApiContract(apiContracts.register, {
      name,
      email,
      password,
      ...(attribution && { attribution: attributionPayload(attribution) }),
      // The landing use case is stored with the account; the template id stays client-side.
      ...(options.useCase ? { useCase: options.useCase } : {}),
    });
    clearSignupAttribution();
    authInitialized = true;
    setUser(payload.user as User);
    setLoading(false);
    return loadWorkspaces();
  }

  /** Reloads the signed-in user from the API, e.g. after a profile change. */
  async function refreshUser(): Promise<User | null> {
    const meRes = await api.get<{ user: User | null }>('/auth/me');
    if (meRes.data.user) setUser(meRes.data.user);
    return meRes.data.user;
  }

  async function updateProfile(input: { name: string }): Promise<User> {
    const res = await api.patch<{ user: User }>('/auth/me', input);
    setUser({ ...res.data.user, impersonator: user?.impersonator ?? null });
    return res.data.user;
  }

  /** Changes (or, for Google/Apple-only accounts, sets) the password; other devices are signed out. */
  /** `verificationCode`: the emailed step-up code (sent when the change needs re-verification). */
  async function changePassword(input: { currentPassword?: string; newPassword: string; verificationCode?: string }): Promise<User> {
    const res = await api.post<{ token: string; user: User }>('/auth/change-password', input);
    // The API re-issued this device's session cookie, so the refreshed user is still signed in.
    setUser({ ...res.data.user, impersonator: user?.impersonator ?? null });
    return res.data.user;
  }

  /** Sends a confirmation link to the new address; the email only changes once that link is opened. */
  async function requestEmailChange(input: { newEmail: string; currentPassword?: string; verificationCode?: string }): Promise<{ newEmail: string }> {
    const res = await api.post<{ accepted: true; newEmail: string }>('/auth/change-email/request', input);
    return { newEmail: res.data.newEmail };
  }

  async function logout() {
    authInitialized = false; // allow re-init after logout
    await api.post('/auth/logout');
    clearStoredEmbedSession();
    setIsEmbedded(false);
    setEmbedBearerToken(null);
    setEmbedContext(null);
    setEmbedError(null);
    setUser(null);
    window.location.href = LANDING_URL;
  }

  return {
    user,
    isLoading,
    isEmbedded,
    embedContext,
    embedError,
    useMockData,
    setUseMockData,
    login,
    loginWithEmail,
    registerWithEmail,
    refreshUser,
    updateProfile,
    changePassword,
    requestEmailChange,
    logout,
  };
}
